import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Dialog, DialogContent, Grid, IconButton, Typography } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faMagnifyingGlass, faTrash, faXmark } from '@fortawesome/free-solid-svg-icons';

import { useAppContext } from '../../Context';
import { useGroups, usePermissions } from '../../services';
import { useAppDispatch } from '../../hooks';
import { setPopConfirmation } from '../../slices/miscSlice';
import { constants } from '../../constants';
import DataTable from '../../components/shared/DataTable';

type RepositionType = {
  id: number;
  order_process?: any;
  order_id?: number;
  process_origin?: any;
  process_origin_name?: string;
  reason?: string;
  created_at?: string;
  created_by?: any;
  created_by_name?: string;
  evidences?: Array<{ file?: string } | string>;
};

type ViewRepositionsProps = {
  endpoint?: string;
  title?: string;
};

const ViewRepositions: React.FC<ViewRepositionsProps> = ({
  endpoint = 'repositions/',
  title = 'Reposiciones',
}) => {
  const navigate = useNavigate();
  const { apiCall } = useAppContext();
  const dispatch = useAppDispatch();
  const {
    canView_repositions,
    canView_repositions_all,
    canView_repositions_assigned,
    canDelete_reposition,
    canView_menu_repositions,
    canView_menu_repositions_created,
    canView_menu_repositions_in_process,
    canView_menu_repositions_solved,
  } = usePermissions([
    'view_repositions',
    'view_repositions_all',
    'view_repositions_assigned',
    'delete_reposition',
    'view_menu_repositions',
    'view_menu_repositions_created',
    'view_menu_repositions_in_process',
    'view_menu_repositions_solved',
  ]);
  const { isAdministrador } = useGroups([constants.groups.GROUP_ADMINISTRADOR]);
  const canViewRepositions = canView_repositions || canView_repositions_all || canView_repositions_assigned;
  const canDeleteReposition = canDelete_reposition || isAdministrador;
  const canViewMenuForEndpoint =
    endpoint === 'repositions/'
      ? canView_menu_repositions_created
      : endpoint === 'repositions/inprocess/'
        ? canView_menu_repositions_in_process
        : endpoint === 'repositions/solved/'
          ? canView_menu_repositions_solved
          : canView_menu_repositions;
  const canViewRepositionsAny = canViewRepositions || canView_menu_repositions || canViewMenuForEndpoint;

  const [repositions, setRepositions] = useState<RepositionType[]>([]);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImages, setLightboxImages] = useState<string[]>([]);

  useEffect(() => {
    if (!canViewRepositionsAny) {
      navigate('/');
      return;
    }
    getRepositions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endpoint]);

  const getRepositions = async () => {
    const response = await apiCall('GET', endpoint, null, '', 'No se pudo obtener las reposiciones', true);
    if (response) {
      setRepositions(response as RepositionType[]);
    } else {
      setRepositions([]);
    }
  };

  const normalizeFileUrl = (value: string) => {
    if (value.startsWith('http') || value.startsWith('data:') || value.startsWith('/')) {
      return value;
    }
    return `${process.env.REACT_APP_FILES_URL}${value}`;
  };

  const getEvidenceUrls = (row: RepositionType) => {
    const evidenceItems = Array.isArray(row.evidences) ? row.evidences : [];
    return evidenceItems
      .map((item) => {
        if (!item) return null;
        if (typeof item === 'string') return item;
        if (item.file) return item.file;
        return null;
      })
      .filter(Boolean) as string[];
  };

  const openLightbox = (row: RepositionType) => {
    const urls = getEvidenceUrls(row);
    setLightboxImages(urls);
    setLightboxOpen(true);
  };

  const deleteReposition = async (id: number) => {
    const deleted = await apiCall(
      'DELETE',
      `repositions/${id}/delete/`,
      null,
      'Reposición eliminada',
      'Error al eliminar la reposición',
      true
    );
    if (deleted) {
      setRepositions((prev) => prev.filter((item) => item.id !== id));
    }
  };

  const handleConfirmDeleteReposition = (row: RepositionType) => {
    dispatch(
      setPopConfirmation({
        show: true,
        message: '¿Está seguro que desea eliminar esta reposición? Esta acción no se puede deshacer.',
        alertType: 'danger',
        okFunc: () => deleteReposition(row.id),
        okText: 'Confirmar',
        cancelText: 'No',
      })
    );
  };

  const columns = [
    { title: 'ID', field: 'id' },
    { title: 'Orden', field: 'order_id' },
    { title: 'Zona del daño', field: 'process_origin_name' },
    { title: 'Reportado por', field: 'created_by_name' },
    {
      title: 'Estado',
      field: 'status',
      type: 'decorator',
      decorator: {
        CREADA: 'info',
        Creada: 'info',
        RESUELTA: 'success',
        Resuelta: 'success',
        EN_PROCESO: 'warning',
        'En Proceso': 'warning',
        CANCELADA: 'error',
        Cancelada: 'error',
      },
    },
    canViewRepositionsAny
      ? {
          title: 'Evidencia',
          field: 'evidences',
          type: 'icon_action',
          icon: <FontAwesomeIcon icon={faMagnifyingGlass} />,
          tooltip: (row: RepositionType) =>
            getEvidenceUrls(row).length > 0 ? 'Ver Evidencia' : 'No hay evidencia registrada',
          onClick: (row: RepositionType) => openLightbox(row),
          disabledWhenEmpty: true,
        }
      : null,
    { title: 'Fecha', field: 'created_at', type: 'datetime' },
  ].filter(Boolean) as Array<any>;

  const actions = [
    canViewRepositions
      ? {
          icon: <FontAwesomeIcon icon={faEye} />,
          tooltip: 'Ver',
          onClick: (row: RepositionType) => navigate(`/reposicion/${row.id}`),
        }
      : null,
    canDeleteReposition
      ? {
          icon: <FontAwesomeIcon icon={faTrash} />,
          tooltip: 'Eliminar',
          onClick: (row: RepositionType) => handleConfirmDeleteReposition(row),
        }
      : null,
  ];

  return (
    <div className='container-wrap'>
      <Grid container spacing={2}>
        <Grid item xs={12} className='text-left'>
          <h2 className='text-left'>{title}</h2>
        </Grid>
      </Grid>
      <DataTable data={repositions} columns={columns} actions={actions} />
      <Dialog open={lightboxOpen} onClose={() => setLightboxOpen(false)} maxWidth='md' fullWidth>
        <DialogContent>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <IconButton
              onClick={() => setLightboxOpen(false)}
              sx={{ maxWidth: 100, maxHeight: 100, borderRadius: 0, '&:hover': { color: '#000' } }}
            >
              <FontAwesomeIcon icon={faXmark} />
            </IconButton>
          </Box>
          {lightboxImages.length === 0 ? (
            <Typography variant='body2' color='textSecondary'>
              No hay evidencias adjuntas.
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, justifyContent: 'center' }}>
              {lightboxImages.map((url, index) => (
                <Box key={index} sx={{ width: 220 }}>
                  <img
                    src={normalizeFileUrl(url)}
                    alt={`evidencia_${index}`}
                    style={{ width: '100%', borderRadius: 6 }}
                  />
                </Box>
              ))}
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ViewRepositions;
